<template>
  <div ref="tabsContainer" class="chat-tabs" :class="{ load: isLoading }">
    <ul class="chat-tabs__list" :class="classWrap">
      <li 
        v-for="tab in tabs" 
        :key="tab.id"
        v-show="tab.isShow"
        :class="[{ active: activeTab == tab.id}, classLi]"
        class="chat-tabs__pill"
        @click="activateTab(tab.id)">
          <span class="chat-tabs__link" :class="classLink">
            <p :class="classH5">{{ tab.name }}</p>
            <span v-if="tab.count && tab.count > 0" class="chat-tabs__count" :class="classCount">{{ tab.count }}</span>
          </span>
      </li>
    </ul>
    <div v-if="!mobile" :style="indicatorStyle" class="chat-tabs__indicator"></div>
  </div>
</template>

<script>
import { ref } from "vue";
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation } from "swiper/modules";
import 'swiper/swiper-bundle.css';

export default {
  components: {
      Swiper,
      SwiperSlide,
  },
  props: {
    tabs: {
      type: Array,
      required: true
    },
    classWrap: {
      type: String,
      default: ''
    },
    classLi: {
      type: String,
      default: ''
    },
    classLink: {
      type: String,
      default: ''
    },
    classH5: {
      type: String,
      default: ''
    },
    classCount: {
      type: String,
      default: ''
    },
    mobile: {
      type: Boolean,
      default: false
    },
    activeTab: {
      type: [String, Number],
      default: 1
    }
  },
  data() {
    return {
      isLoading: true,
      indicatorStyle: {},
    };
  },
  setup() {
      const prev = ref(null);
      const next = ref(null);
      return {
          modules: [Navigation],
          prev,
          next,
      };
  },
  watch: {
    tabs() {
      this.updateIndicatorPosition();
    },
    activeTab() {
      this.updateIndicatorPosition();
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.updateIndicatorPosition();
    });
    setTimeout(() => {
      this.isLoading = false;
    }, 700);
  },
  methods: {
    activateTab(id) {
      this.$emit('tab-changed', id);
      this.$nextTick(() => {
        this.updateIndicatorPosition();
      });
    },
    updateIndicatorPosition() {
      this.$nextTick(() => {
        const tabsContainer = this.$refs.tabsContainer;
        if (!tabsContainer) {
          console.warn('Tabs container not found');
          return;
        }

        const activeTab = tabsContainer.querySelector('li.active');
        const indicator = tabsContainer.querySelector('.chat-tabs__indicator');

        if (activeTab && indicator) {
          const activeTabOffset = activeTab.offsetLeft;
          const activeTabWidth = activeTab.offsetWidth;
          
          this.indicatorStyle = {
            width: `${activeTabWidth}px`,
            transform: `translateX(${activeTabOffset}px)`,
            transition: 'transform 0.4s'
          };
        }
      });
    }
  }
};
</script>
  
<style lang="scss">
// Own class names (not .tabs-container / .nav-tabs): the site's TabsIndicator ships global,
// dark-theme rules for those, which used to paint the chat tabs dark while loading.
// Segmented control from the design: 32px track, white sliding pill.
.chat-tabs{
  position: relative;
  padding: 2px;
  border-radius: 8px;
  background: var(--wc-bg);
  &__list{
    display: flex;
    gap: 2px;
    margin: 0;
    padding: 0;
    list-style: none;
    position: relative;
  }
  &__pill{
    position: relative;
    z-index: 2;
    flex: 1;
    height: 28px;
    border-radius: 6px;
  }
  &__link{
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    height: 100%;
    padding: 0 8px;
    border-radius: 6px;
    font-family: var(--wc-font-medium);
    font-weight: 400;
    font-size: 13px;
    line-height: 18px;
    color: var(--wc-text-2);
    cursor: pointer;
    transition: color 0.2s;
    p{
      margin: 0;
      font: inherit;
      color: inherit;
    }
    &:hover{
      color: var(--wc-text);
    }
  }
  &__pill.active &__link{
    color: var(--wc-text);
  }
  &__count{
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: var(--wc-accent-soft);
    color: var(--wc-accent);
    font-size: 11px;
    line-height: 18px;
    text-align: center;
  }
  &__indicator{
    position: absolute;
    top: 2px;
    bottom: 2px;
    // offsetLeft is measured inside the list, which sits in the 2px track padding
    left: 2px;
    border-radius: 6px;
    background: var(--wc-surface);
    box-shadow: 0 1px 2px rgba(15, 23, 41, 0.08);
  }
  // before the indicator is measured, the active pill itself is the white pill
  &.load{
    .chat-tabs__indicator{
      background: transparent;
      box-shadow: none;
    }
    .chat-tabs__pill.active .chat-tabs__link{
      background: var(--wc-surface);
      box-shadow: 0 1px 2px rgba(15, 23, 41, 0.08);
    }
  }
}
</style>
